"use client";

// ─────────────────────────────────────────────────────────────────────────
// "We left for Stripe with this selection" — the stash that restores the
// credits & plans modal when the user comes back without paying.
//
// Hosted Stripe Checkout is a full-page navigation (rated-credits-flow's
// checkout() ends in window.location.assign). Panda's React state — including
// `showRatedCreditsModal` and the plan/credit amount the user picked — dies
// with the page. Browser-back from Stripe lands on the bare return URL with no
// query flag (cancel_url is a plain return: see subscribe-rated/upgrade-rated/
// topup-rated), so nothing told the host to reopen anything, and the user
// arrived back at the project with the modal gone and their selection lost.
//
// This is the same trick as lib/domains/domain-upgrade-return.ts, generalised:
// that module records a bare "did we leave?" bit for the domain flow, while
// this one records WHAT the user was buying, so the flow can reopen on the
// screen they left from with their plan/amount still selected.
//
// Written at the one place Panda hands off to Stripe (rated-credits-flow's
// checkout, right before the redirect) and consumed by the modal hosts through
// useCheckoutIntentResume. Because it is only ever written on a real redirect,
// a user who simply closes the modal leaves nothing behind to reopen.
//
// sessionStorage, not localStorage: the stash describes one tab's round-trip to
// Stripe and back. In localStorage it would leak into other tabs and outlive
// the trip it belongs to.
// ─────────────────────────────────────────────────────────────────────────

import type { PaidPlanId } from "@/lib/plans";

const KEY = "panda:checkout-intent";

// A checkout round-trip is minutes, not hours. Long enough to survive card
// hunting, 3DS detours, and a phone unlock; short enough that a stash which
// somehow escapes both consume paths can't reopen the modal much later.
const TTL_MS = 60 * 60 * 1000;

/**
 * Which modal the user left from. Two separate surfaces redirect to Stripe —
 * the rated credits & plans flow, and the credit top-up panel (Growth chat, the
 * Growth canvas banner, and non-rated billing) — and they must not restore each
 * other: an add-credits stash carries no rated screen, and a rated stash means
 * nothing to the top-up slider.
 */
export type CheckoutSurface = "rated" | "addCredits";

export interface CheckoutIntent {
  surface: CheckoutSurface;
  /** The rated flow's screen — where they go back to. Unused by "addCredits",
   *  which is a single panel. */
  screen?: string;
  /** Plan they were subscribing/upgrading to (absent for one-off top-ups). */
  plan?: PaidPlanId;
  /** Credits selected: the monthly allocation, or the top-up amount. */
  credits?: number;
  /** Entry-point attribution, preserved so the resumed checkout stays tagged. */
  source?: string;
  intent?: string;
  /**
   * Where this stash may be honoured: the page the user launched checkout from
   * (where browser-back lands) and the checkout's return URL (where Stripe's own
   * cancel button lands). Usually the same page; they diverge when a host passes
   * an explicit `returnTo`. Restricting to these keeps a round-trip started on
   * the project page from popping the modal open in Settings.
   */
  paths: string[];
  timestamp: number;
}

/** Call immediately before navigating to hosted Stripe Checkout. */
export function stashCheckoutIntent(
  intent: Omit<CheckoutIntent, "timestamp">,
): void {
  try {
    sessionStorage.setItem(
      KEY,
      JSON.stringify({ ...intent, timestamp: Date.now() } satisfies CheckoutIntent),
    );
  } catch {
    // Private mode / quota. The round-trip still works, the user just doesn't
    // get their selection back — never worth breaking the checkout over.
  }
}

/**
 * Read AND clear the stash. One-shot by design: the modal reopens once per
 * departure, so a later revisit to the same page in this tab can't be mistaken
 * for another return from Stripe.
 *
 * Returns null when there is nothing for THIS caller to restore: no stash, a
 * malformed one, one past its TTL, or one belonging to a different page or a
 * different surface. Malformed/expired stashes are dropped on the way out;
 * stashes belonging to someone else are left for them.
 */
export function consumeCheckoutIntent(
  path: string,
  surface: CheckoutSurface,
): CheckoutIntent | null {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: Partial<CheckoutIntent> | null = null;
  try {
    parsed = JSON.parse(raw) as Partial<CheckoutIntent>;
  } catch {
    clearCheckoutIntent();
    return null;
  }

  if (
    !parsed ||
    (parsed.surface !== "rated" && parsed.surface !== "addCredits") ||
    !Array.isArray(parsed.paths) ||
    typeof parsed.timestamp !== "number" ||
    Date.now() - parsed.timestamp > TTL_MS
  ) {
    clearCheckoutIntent();
    return null;
  }

  // Belongs to another page or another modal: leave it alone rather than
  // dropping it, so whoever it does belong to still gets their chance to
  // restore. (Both can be mounted at once — the billing page has the rated card
  // AND the top-up panel.)
  if (!parsed.paths.includes(path)) return null;
  if (parsed.surface !== surface) return null;

  clearCheckoutIntent();
  return {
    surface: parsed.surface,
    screen: parsed.screen,
    plan: parsed.plan,
    credits: typeof parsed.credits === "number" ? parsed.credits : undefined,
    source: parsed.source,
    intent: parsed.intent,
    paths: parsed.paths,
    timestamp: parsed.timestamp,
  };
}

/**
 * Drop the stash without acting on it. Used by the paths that own the return
 * themselves — a completed purchase (?upgraded / ?topup, which show the
 * confirmation instead) and an in-app dismiss of the modal — so neither leaves
 * anything behind for the reopen path to pick up.
 */
export function clearCheckoutIntent(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {}
}

/**
 * The query flags Stripe returns on a COMPLETED purchase (and the saved-card
 * path that mimics them). Their presence means the host is already showing the
 * success/confirmation state, so the modal must not reopen over it.
 */
export function hasCheckoutSuccessParam(search: string): boolean {
  const params = new URLSearchParams(search);
  return (
    params.get("upgraded") === "true" ||
    params.get("topup") === "true" ||
    params.get("card_added") === "true" ||
    params.has("subscribed")
  );
}

/**
 * True when the current query string is a return from ANY Stripe checkout
 * redirect — the completed-purchase markers (see hasCheckoutSuccessParam) plus
 * the $1 trial start (`?trial=started`, which hasCheckoutSuccessParam omits
 * because the trial modal owns its own reopen logic). A redirect return is a
 * plain reload of the app the user was already in, so the loading state should
 * be neutral ("reopen") rather than the "Restoring your session" card.
 */
export function isCheckoutReturn(search: string): boolean {
  return (
    hasCheckoutSuccessParam(search) ||
    new URLSearchParams(search).get("trial") === "started"
  );
}
